import React, { useEffect, useState } from "react";
import { adminAnalytics } from "@/lib/api";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Inbox, CalendarCheck, CircleCheck, PhoneForwarded, MousePointerClick } from "lucide-react";

const STATUS_COLORS = {
    new: "#1D4ED8",
    contacted: "#3E7A5D",
    callback: "#D9A84B",
    successful: "#2E5A3E",
    lost: "#9A4040",
};

const CHANNEL_LABELS = {
    whatsapp: "WhatsApp",
    call: "Phone call",
    message: "Leave a message",
    ai_helper: "AI Finance Helper",
    calendly: "Calendly booking",
};
const CHANNEL_COLORS = {
    whatsapp: "#25D366",
    call: "#FF7A1A",
    message: "#1E4F8A",
    ai_helper: "#9333EA",
    calendly: "#0EA5A4",
};

export default function AdminAnalytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        adminAnalytics(days)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [days]);

    if (loading || !data) {
        return <div className="text-brand-muted text-sm">Loading analytics…</div>;
    }

    const { totals, by_status, by_source, by_service, by_channel, by_channel_recent, series } = data;

    const statusData = Object.entries(by_status || {}).map(([k, v]) => ({
        name: k,
        value: v,
        color: STATUS_COLORS[k] || "#888",
    }));
    const sourceData = Object.entries(by_source || {}).map(([k, v]) => ({
        name: k.replace(/_/g, " "),
        value: v,
    }));
    const serviceData = Object.entries(by_service || {}).map(([k, v]) => ({
        name: k,
        value: v,
    }));
    const channelData = Object.entries(by_channel_recent || {})
        .map(([k, v]) => ({
            key: k,
            name: CHANNEL_LABELS[k] || k,
            value: v,
            color: CHANNEL_COLORS[k] || "#888",
        }))
        .sort((a, b) => b.value - a.value);
    const channelTotalRecent = channelData.reduce((s, d) => s + d.value, 0);

    return (
        <div data-testid="admin-analytics" className="space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                        Overview
                    </div>
                    <h1 className="font-serif-editorial text-4xl text-brand-primary mt-1">
                        Analytics
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            data-testid={`analytics-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                days === d
                                    ? "bg-brand-primary text-brand-bg"
                                    : "bg-brand-surface border border-brand-border text-brand-muted hover:border-brand-primary"
                            }`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Stat label="Total Leads" value={totals.leads} icon={Inbox} sub={`${totals.leads_recent} in window`} />
                <Stat label="Consultations" value={totals.consultations} icon={CalendarCheck} sub={`${totals.consultations_recent} in window`} />
                <Stat label="Contacted" value={totals.contacted} icon={PhoneForwarded} />
                <Stat label="Callbacks" value={totals.callbacks} icon={PhoneForwarded} />
                <Stat label="Successful" value={totals.successful} icon={CircleCheck} sub={`${totals.conversion_rate_pct}% conversion`} accent />
            </div>

            <ChartCard title={`Channel intents — which CTA visitors clicked (${days}d)`}>
                {channelTotalRecent === 0 ? (
                    <div data-testid="intents-empty" className="text-brand-muted text-sm py-6 text-center">
                        No channel clicks yet. Once visitors tap WhatsApp, Call, AI Helper or
                        Leave-a-message in the chat hub, the breakdown shows here.
                    </div>
                ) : (
                    <div data-testid="intents-card" className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {channelData.map((c) => {
                            const pct = channelTotalRecent ? Math.round((c.value / channelTotalRecent) * 100) : 0;
                            return (
                                <div
                                    key={c.key}
                                    data-testid={`intent-${c.key}`}
                                    className="p-4 border border-brand-border bg-brand-bg/40 flex flex-col gap-2"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="h-2.5 w-2.5 rounded-full"
                                            style={{ background: c.color }}
                                        />
                                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">
                                            {c.name}
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-serif-editorial text-3xl text-brand-primary leading-none">
                                            {c.value}
                                        </span>
                                        <span className="text-xs text-brand-muted">{pct}%</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-brand-border/60 overflow-hidden rounded-full">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${pct}%`, background: c.color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="mt-3 flex items-center gap-2 text-[11px] text-brand-muted">
                    <MousePointerClick size={12} />
                    Counts every click on a contact CTA (no PII stored). Total clicks all-time:{" "}
                    <strong className="text-brand-primary">{totals.intents || 0}</strong>.
                </div>
            </ChartCard>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ChartCard title="Leads & Consultations — time series" className="lg:col-span-2">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2DED1" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                            <Tooltip />
                            <Line type="monotone" dataKey="leads" stroke="#1E4F8A" strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="consultations" stroke="#1D4ED8" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Leads by status">
                    <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                            <Pie
                                data={statusData.length ? statusData : [{ name: "none", value: 1, color: "#DDD" }]}
                                dataKey="value"
                                innerRadius={55}
                                outerRadius={90}
                                paddingAngle={2}
                            >
                                {(statusData.length ? statusData : [{ color: "#DDD" }]).map((d, i) => (
                                    <Cell key={i} fill={d.color} />
                                ))}
                            </Pie>
                            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ChartCard title="Leads by source">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={sourceData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2DED1" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#1E4F8A" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Top services / topics">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={serviceData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2DED1" />
                            <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#1D4ED8" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, sub, accent }) {
    return (
        <div
            className={`p-5 border ${accent ? "bg-brand-primary text-brand-bg border-brand-primary" : "bg-brand-surface border-brand-border text-brand-primary"}`}
        >
            <div className="flex items-center justify-between">
                <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${accent ? "text-brand-accent" : "text-brand-muted"}`}>
                    {label}
                </div>
                <Icon size={14} className={accent ? "text-brand-accent" : "text-brand-muted"} />
            </div>
            <div className="mt-3 font-serif-editorial text-4xl leading-none">
                {value ?? 0}
            </div>
            {sub && (
                <div className={`mt-2 text-xs ${accent ? "text-white/75" : "text-brand-muted"}`}>
                    {sub}
                </div>
            )}
        </div>
    );
}

function ChartCard({ title, children, className = "" }) {
    return (
        <div className={`bg-brand-surface border border-brand-border p-5 ${className}`}>
            <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-brand-accent" />
                <h3 className="text-sm font-bold text-brand-primary">{title}</h3>
            </div>
            {children}
        </div>
    );
}
